import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import { Form, Row, Col } from 'react-bootstrap';
import AnimateHeight from 'react-animate-height';

import { isPluginActive } from 'helpers/whiteLabel';
import { clientSingleOptionFormatter, clientOptionFromEntity } from 'components/forms/autocomplete/Client';

import Loader from 'components/layout/Loader';
import SimpleSubmitButton from 'components/layout/SimpleSubmitButton';
import Data from 'components/Data';
import Entity from 'components/Entity';

import FormAutocomplete from 'components/forms/FormAutocomplete';
import FormInput from 'components/forms/FormInput';
import FormInputPassword from 'components/forms/FormInputPassword';
import FormRadio from 'components/forms/FormRadio';
import FormErrors from 'components/forms/FormErrors';

import EntityStateUpdater from 'component/EntityStateUpdater';

import may from 'js/security';

import { entityMetaMapping } from 'js/constants';

class AccountForm extends EntityStateUpdater {
  constructor(props) {
    super(props);

    const { user, projectConfig } = props;

    const isAdmin = may('ROLE_ADMIN', user.roles);

    const typeOptions = [];

    if (isAdmin) {
      typeOptions.push({ label: 'Mobile.de (Seller-API)', value: 'mobiledeSellerApi' });

      if (isPluginActive('CUSTOMER_RETENTION_PC', projectConfig.data)) {
        typeOptions.push({ label: 'PartnerConnect', value: 'partnerconnect' });
      }
      if (
        isPluginActive('RENT_ACTIVITY_DASHBOARD', projectConfig.data) ||
        isPluginActive('RENT_SYNC_VAMOSO', projectConfig.data)
      ) {
        typeOptions.push({ label: 'Vamoso', value: 'vamoso' });
      }
      if (isPluginActive('SALES_ACTIVITY_DASHBOARD', projectConfig.data)) {
        typeOptions.push({ label: 'Onetouchretail', value: 'onetouchretail' });
      }
      if (isPluginActive('CONNECTME', projectConfig.data)) {
        typeOptions.push({ label: 'Connectme', value: 'connectme' });
      }
      if (isPluginActive('GUDAT_SOLUTIONS_DA', projectConfig.data)) {
        typeOptions.push({ label: 'Gudat Solutions - Digitales Autohaus', value: 'gudatSolutionsDa' });
      }
    }

    if (!isAdmin) {
      if (isPluginActive('SALES_ACTIVITY_DASHBOARD', projectConfig.data)) {
        typeOptions.push({ label: 'Onetouchretail', value: 'onetouchretail' });
      }
    }

    this.state = {
      entityLoaded: false,
      entityChanged: false,
      typeOptions,
    };

    this.entity = new Entity();
    // if we get account data from the outside, we can pass it on to our data entity
    this.entity.setData({ type: typeOptions[0].value, ...props.account } || { type: typeOptions[0].value });

    if (
      props.meta[entityMetaMapping.externalAccount] !== undefined &&
      !props.meta[entityMetaMapping.externalAccount].pending
    ) {
      this.entity.setMeta(props.meta, entityMetaMapping.externalAccount);

      this.state = {
        ...this.state,
        entityLoaded: true,
        entity: this.entity,
      };
    }
  }

  componentDidUpdate = () => {
    const { meta } = this.props;

    if (
      meta[entityMetaMapping.externalAccount] !== undefined &&
      !meta[entityMetaMapping.externalAccount].pending &&
      !this.entity.getMetaLoaded()
    ) {
      this.entity.setMeta(meta, entityMetaMapping.externalAccount);
      if (this.entity.getDataLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }
    }
  };

  formSubmit = e => {
    e.preventDefault();
    e.stopPropagation();

    this.setState(
      prevState => ({ entity: prevState.entity.validate() }),
      () => {
        if (this.state.entity.isValid) {
          let data = this.state.entity.processedData;
          if (this.props.requestData) {
            data = { ...data, ...this.props.requestData };
          }

          this.props.onSubmit(data);
        }
      }
    );
  };

  render() {
    const {
      intl: { messages },
      formStatus,
      requestData,
      user,
    } = this.props;
    const { entityLoaded, entity, entityChanged, typeOptions } = this.state;

    const isAdmin = may('ROLE_ADMIN', user.roles);

    let clientOption;
    if (entityLoaded) {
      clientOption = !isAdmin ? clientSingleOptionFormatter(user) : clientOptionFromEntity(entity.getTarget());
    }

    return (
      <Row>
        <Data dataName="externalAccount" /> {/* fetch meta data */}
        <Col xs={12}>
          <FormErrors formStatus={formStatus} />

          {entityLoaded ? (
            <Form method="post" onSubmit={this.formSubmit}>
              <div class="panel panel-transparent">
                <div class="form-group-attached">
                  <FormRadio
                    label={messages.type}
                    name="type"
                    options={typeOptions}
                    onChange={this.updateEntity}
                    value={entity.getType()}
                    meta={entity.getTypeMetaInformation()}
                    validation={entity.getTypeValidationResult()}
                    disabled={formStatus.pending}
                  />
                  {entity.getType() === 'onetouchretail' && isAdmin && (
                    <FormAutocomplete
                      label={messages.employee}
                      name="target"
                      value={clientOption}
                      meta={entity.getTargetMetaInformation()}
                      validation={entity.getTargetValidationResult()}
                      onChange={this.updateEntity}
                      returnValueObject
                      autocompleteType="user"
                      disabled={!isAdmin || formStatus.pending}
                    />
                  )}
                  {entity.getType() === 'mobiledeSellerApi' && (
                    <FormAutocomplete
                      label={messages.location}
                      name="location"
                      value={
                        entity.getLocation()
                          ? {
                              ...entity.getLocation(),
                              value: entity.getLocation().getId(),
                              label: entity.getLocation().getName(),
                            }
                          : null
                      }
                      meta={entity.getLocationMetaInformation()}
                      validation={entity.getLocationValidationResult()}
                      onChange={this.updateEntity}
                      returnValueObject
                      autocompleteType="companyLocation"
                      urlFilters={requestData.owner ? { filters: { owner: requestData.owner } } : null}
                      complete
                      disabled={formStatus.pending}
                    />
                  )}
                  <FormInput
                    name="name"
                    label={messages.name}
                    value={entity.getName()}
                    meta={entity.getNameMetaInformation()}
                    validation={entity.getNameValidationResult()}
                    onChange={this.updateEntity}
                    disabled={formStatus.pending}
                  />
                  <FormInput
                    name="username"
                    label={messages.username}
                    value={entity.getUsername()}
                    meta={entity.getUsernameMetaInformation()}
                    validation={entity.getUsernameValidationResult()}
                    onChange={this.updateEntity}
                    disabled={formStatus.pending}
                  />
                  <FormInputPassword
                    name="password"
                    label={messages.password}
                    value={entity.getPassword()}
                    meta={entity.getPasswordMetaInformation()}
                    validation={entity.getPasswordValidationResult()}
                    onChange={this.updateEntity}
                    disabled={formStatus.pending}
                  />
                </div>

                <AnimateHeight duration={300} height={entity.getType() === 'vamoso' ? 'auto' : 0}>
                  <div>
                    <p class="m-t-15 form-group-title">Vamoso Daten</p>
                    <div class="form-group-attached">
                      <FormInput
                        name="vamosoAccessIdentifier"
                        label={'Vamoso - ' + messages.access_identifier}
                        value={entity.getVamosoAccessIdentifier()}
                        meta={entity.getVamosoAccessIdentifierMetaInformation()}
                        validation={entity.getVamosoAccessIdentifierValidationResult()}
                        onChange={this.updateEntity}
                        disabled={formStatus.pending}
                      />
                      <FormInput
                        name="vamosoBranch"
                        label={'Vamoso - ' + messages.branch}
                        value={entity.getVamosoBranch()}
                        meta={entity.getVamosoBranchMetaInformation()}
                        validation={entity.getVamosoBranchValidationResult()}
                        onChange={this.updateEntity}
                        disabled={formStatus.pending}
                      />
                    </div>
                  </div>
                </AnimateHeight>

                <AnimateHeight duration={300} height={entity.getType() === 'gudatSolutionsDa' ? 'auto' : 0}>
                  <div>
                    <p class="m-t-15 form-group-title">Gudat Solutions - Digitales Autohaus Daten</p>
                    <div class="form-group-attached">
                      <FormInput
                        name="gudatSolutionsGroup"
                        label="Digitales Autohaus - Group"
                        value={entity.getGudatSolutionsGroup()}
                        meta={entity.getGudatSolutionsGroupMetaInformation()}
                        validation={entity.getGudatSolutionsGroupValidationResult()}
                        onChange={this.updateEntity}
                        disabled={formStatus.pending}
                      />
                      <FormInput
                        name="gudatSolutionsCenter"
                        label="Digitales Autohaus - Center"
                        value={entity.getGudatSolutionsCenter()}
                        meta={entity.getGudatSolutionsCenterMetaInformation()}
                        validation={entity.getGudatSolutionsCenterValidationResult()}
                        onChange={this.updateEntity}
                        disabled={formStatus.pending}
                      />
                    </div>
                  </div>
                </AnimateHeight>

                <div class="text-right m-t-15">
                  <SimpleSubmitButton
                    label={messages.save}
                    entityChanged={entityChanged}
                    errorButton={false}
                    pending={formStatus.pending}
                  />
                </div>
              </div>
            </Form>
          ) : (
            <Loader />
          )}
        </Col>
      </Row>
    );
  }
}

const mapStateToProps = state => {
  return {
    meta: state.entityMeta,
    user: state.login.user,
    projectConfig: state.data.projectConfig,
  };
};

export default withRouter(injectIntl(connect(mapStateToProps)(AccountForm)));
